import { graphql } from 'gatsby';
import { applyPaddingCSS } from '@/utils/generatePadding';
import {
  StyledSection,
  StyledContainerN5050,
  StyledRow,
  StyledHeader,
  StyledEyebrow,
  StyledHeadline,
  StyledDescription,
  StyledButton,
  StyledHeaderContent,
} from './N5050Block.styles';
import { type BlockHTMLId } from '@/utils/renderBlock';
import { lazy, Suspense } from 'react';
const N5050BlockImageAndTextRow = lazy(
  () => import('./components/N5050BlockImageAndTextRow/N5050BlockImageAndTextRow')
);
const N5050BlockVideoAndTextRow = lazy(
  () => import('./components/N5050BlockVideoAndTextRow/N5050BlockVideoAndTextRow')
);
const N5050BlockTextRow = lazy(() => import('./components/N5050BlockTextRow/N5050BlockTextRow'));
const N5050BlockCampaignRow = lazy(
  () => import('./components/N5050BlockCampaignRow/N5050BlockCampaignRow')
);
const N5050BlockImageAndAccordionRow = lazy(
  () => import('./components/N5050BlockImageAndAccordionRow/N5050BlockImageAndAccordionRow')
);
const N5050BlockFeatureList = lazy(
  () => import('./components/N5050BlockFeatureList/N5050BlockFeatureList')
);

const renderRow = (
  row: Queries.N5050_BLOCKFragment['n5050BlockElements']['items']['value'][number]
) => {
  switch (row.__typename) {
    case 'kontent_item_n5050_block_image_and_text_row':
      return <N5050BlockImageAndTextRow {...row} />;
    case 'kontent_item_n5050_block_video_and_text_row':
      return <N5050BlockVideoAndTextRow {...row} />;
    case 'kontent_item_n5050_block_image_and_accordion_row':
      return <N5050BlockImageAndAccordionRow {...row} />;
    case 'kontent_item_n5050_block_text_row':
      return <N5050BlockTextRow {...row} />;
    case 'kontent_item_n5050_block_campaign_row':
      return <N5050BlockCampaignRow {...row} />;
    case 'kontent_item_n5050_block_feature_list':
      return <N5050BlockFeatureList {...row} />;
  }
};

const N5050Block = ({
  n5050BlockElements: elements,
  system,
  id,
}: Queries.N5050_BLOCKFragment & BlockHTMLId) => {
  const { cta_button, eyebrow, headline, introduction, items } = elements;

  const showHeader = Boolean(
    eyebrow?.value || headline?.value || introduction?.value || cta_button?.value
  );

  return (
    <StyledSection data-codename={system.codename} css={applyPaddingCSS(elements)} id={id}>
      <StyledContainerN5050>
        {showHeader && (
          <StyledHeader>
            <StyledHeaderContent>
              {eyebrow?.value && <StyledEyebrow>{eyebrow.value}</StyledEyebrow>}
              {headline?.value && <StyledHeadline>{headline.value}</StyledHeadline>}
              {introduction?.value && <StyledDescription>{introduction.value}</StyledDescription>}
              {cta_button?.value && <StyledButton {...cta_button.value} variant="primary" />}
            </StyledHeaderContent>
          </StyledHeader>
        )}
        {items.value.map((item) => {
          let direction = undefined;
          let backgroundColor = undefined;

          if (
            item.__typename === 'kontent_item_n5050_block_image_and_text_row' &&
            'n5050BlockImageTextRowElements' in item
          ) {
            direction = item.n5050BlockImageTextRowElements.image_direction?.value;
            backgroundColor = item.n5050BlockImageTextRowElements.background_color?.value;
          } else if (
            item.__typename === 'kontent_item_n5050_block_video_and_text_row' &&
            'n5050BlockVideoTextRowElements' in item
          ) {
            direction = item.n5050BlockVideoTextRowElements.video_direction?.value;
            backgroundColor = item.n5050BlockVideoTextRowElements.background_color?.value;
          } else if (
            item.__typename === 'kontent_item_n5050_block_image_and_accordion_row' &&
            'elements' in item
          ) {
            direction = item.elements.accordionImageDirection?.value;
            backgroundColor = item.elements.background_color?.value;
          }

          return (
            <StyledRow
              key={item.system.id}
              image_direction={direction}
              background_color={backgroundColor ?? undefined}
            >
              <Suspense>{renderRow(item)}</Suspense>
            </StyledRow>
          );
        })}
      </StyledContainerN5050>
    </StyledSection>
  );
};

export default N5050Block;

export const fragment = graphql`
  fragment N5050_BLOCK on kontent_item_n5050_block {
    __typename
    system {
      codename
      type
      id
    }
    n5050BlockElements: elements {
      ...COMPONENT_A_B_TEST
      ...COMPONENT_PERSONALIZATION
      ...PADDING
      eyebrow {
        value
      }
      headline {
        value
      }
      introduction {
        value
      }
      cta_button {
        value {
          ...LINK
        }
      }
      items {
        value {
          __typename
          ...N5050_BLOCK_TEXT_ROW
          ...N5050_BLOCK_IMAGE_AND_TEXT_ROW
          ...N5050_BLOCK_VIDEO_AND_TEXT_ROW
          ...N5050_BLOCK_CAMPAIGN_ROW
          ...N5050_BLOCK_IMAGE_AND_ACCORDION_ROW
          ...N5050_BLOCK_FEATURE_LIST
        }
      }
    }
  }
`;
